import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, ListTree, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent, Badge } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { productionService } from '@services/productionService';
import { BillOfMaterial } from '@/types/production';

/**
 * Listagem de Listas de Materiais (BOM) do módulo de produção
 */
export const BomListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<BillOfMaterial[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Exclusão
  const [deleteTarget, setDeleteTarget] = useState<BillOfMaterial | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await productionService.listBillOfMaterials({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
      });

      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar listas de materiais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadItems();
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await productionService.deleteBillOfMaterial(deleteTarget.id);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir lista de materiais');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Lista de Materiais (BOM)</h1>
          <p className="text-slate-600">
            Composição de materiais para produtos de fabricação própria e materiais compostos
          </p>
        </div>
        <PermissionGuard permission="production.create">
          <Button onClick={() => navigate('/production/bom/new')} icon={<Plus size={20} />}>
            Nova Lista de Materiais
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros e busca */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por código ou descrição do item..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={<Search size={20} />}
              />
            </div>
            <div>
              <select
                className="input"
                value={statusFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                }}
              >
                <option value="active">Ativas</option>
                <option value="inactive">Inativas</option>
                <option value="all">Todas</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={18} />
              <p className="text-red-600">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando listas de materiais...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <ListTree className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search
                  ? 'Nenhuma lista de materiais encontrada'
                  : 'Nenhuma lista de materiais cadastrada'}
              </p>
              <PermissionGuard permission="production.create">
                <Button onClick={() => navigate('/production/bom/new')}>
                  Cadastrar primeira lista de materiais
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Código</th>
                  <th className="text-left">Descrição</th>
                  <th className="text-center">Tipo</th>
                  <th className="text-right">Qtde de Saída</th>
                  <th className="text-center">Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                        {item.parent_code || '-'}
                      </code>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium text-slate-900">
                        {item.parent_description || '-'}
                      </p>
                    </TableCell>
                    <TableCell className="text-center">
                      {item.product_id ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                          Produto
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Material
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium">
                        {item.output_quantity?.toLocaleString('pt-BR') ?? '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={item.is_active ? 'success' : 'danger'}>
                        {item.is_active ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="production.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/production/bom/${item.id}/edit`)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="production.delete">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteTarget(item)}
                            icon={<Trash2 size={14} />}
                          >
                            Excluir
                          </Button>
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">Total: {totalItems} registro(s)</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === 1 || loading}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage === totalPages || loading}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Excluir Lista de Materiais"
        message={`Tem certeza que deseja excluir a lista de materiais de "${
          deleteTarget?.parent_code || ''
        } - ${deleteTarget?.parent_description || ''}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleting}
      />
    </div>
  );
};
